import { useEffect, useRef, useState } from "react";

const ContactItem = ({ icon, title, content, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const itemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={itemRef}
      className={`contact-item transition-all duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="contact-icon">{icon}</div>
      <div className="contact-content">
        <h3 className="mb-2 text-lg font-medium header-color">{title}</h3>
        <div className="text-gray-700">{content}</div>
      </div>
    </div>
  );
};

const Contact = () => {
  // Добавляем структурированные данные для контактов
  useEffect(() => {
    const contactStructuredData = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Контакты Лесной Усадьбы в Солнечногорском районе",
      "description": "Свяжитесь с нами для бронирования фотосессии в Лесной Усадьбе в Солнечногорском районе Подмосковья.",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://fotodacha.ru/#contacts"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Лесная Усадьба",
        "logo": {
          "@type": "ImageObject",
          "url": "https://fotodacha.ru/favicon/android-icon-192x192.png"
        }
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "areaServed": ["RU", "Московская область", "Солнечногорский район"],
        "availableLanguage": ["Russian"]
      },
      "keywords": "фотолокация, фотосессия, Подмосковье, Солнечногорский район, Московская область, контакты, бронирование"
    };

    // Добавляем структурированные данные
    let scriptTag = document.querySelector('#contact-structured-data');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'contact-structured-data';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(contactStructuredData);

    return () => {
      if (scriptTag && document.head.contains(scriptTag)) {
        document.head.removeChild(scriptTag);
      }
    };
  }, []);

  return (
    <section id="contacts" className="section bg-white py-16">
      <div className="container">
        <h2 className="mb-12 text-3xl font-bold text-center font-serif header-color md:text-4xl">
          Контакты
        </h2>

        <div className="grid grid-cols-1 gap-8 md:gap-12 lg:grid-cols-2">
          <div className="space-y-8">
            <ContactItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9.417 15.181l-.397 5.584c.568 0 .814-.244 1.109-.537l2.663-2.545 5.518 4.041c1.012.564 1.725.267 1.998-.931l3.622-16.972.001-.001c.321-1.496-.541-2.081-1.527-1.714l-21.29 8.151c-1.453.564-1.431 1.374-.247 1.741l5.443 1.693 12.643-7.911c.595-.394 1.136-.176.691.218z" />
                </svg>
              }
              title="Telegram"
              content={
                <p>
                  <a href="https://t.me/Giulia_Ko" target="_blank" rel="noopener noreferrer">
                    @Giulia_Ko
                  </a>
                </p>
              }
              delay={450}
            />

            {/* <ContactItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title="WhatsApp"
              content={<p>...</p>}
              delay={450}
            /> */}

            <ContactItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title="Часы работы"
              content={
                <p>
                  Ежедневно с 9:00 до 21:00 <br />
                  по предварительной записи
                </p>
              }
              delay={450}
            />

            <ContactItem
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              }
              title="Адрес"
              content={
                <p>
                  Солнечногорский район, <br />
                  Московская область <br />
                  <span className="text-sm text-gray-500">(точный адрес высылается после бронирования)</span>
                </p>
              }
              delay={600}
            />
          </div>

          <div className="bg-linen rounded-lg p-6 shadow-md">
            <h3 className="text-xl font-bold font-serif header-color mb-4">Как добраться</h3>
            <p className="mb-4 text-gray-700">
              Наша фотолокация находится в Солнечногорском районе Московской области, примерно в 60 км от МКАД.
            </p>
            <p className="mb-4 text-gray-700">
              <strong>На автомобиле:</strong> Удобный подъезд по Ленинградскому шоссе. Время в пути от Москвы - около 1 часа.
            </p>
            <p className="text-gray-700">
              <strong>Общественным транспортом:</strong> Электричка с Ленинградского вокзала до станции Подсолнечная, далее такси до места (около 15 минут).
            </p>
            <div className="mt-4 text-sm text-gray-500">
              Точные координаты и схема проезда предоставляются после бронирования.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
